const S = ({ className = '' }: { className?: string }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
);

const NavItemSkeleton = ({ indent = false }: { indent?: boolean }) => (
  <div className={`flex items-center gap-2.5 py-1.5 px-2 ${indent ? 'ml-2' : ''}`}>
    <S className={`flex-shrink-0 rounded-full ${indent ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}`} />
    <S className={`${indent ? 'h-3 w-24' : 'h-3.5 w-32'}`} />
  </div>
);

const MatchedProviderSkeleton = ({ avatarSize = 'h-14 w-14' }: { avatarSize?: string }) => (
  <div className="rounded-xl border border-gray-200 p-4 mb-4">
    <div className="flex gap-4 mb-3">
      <S className={`${avatarSize} rounded-full flex-shrink-0`} />
      <div>
        <S className="h-4 w-36 mb-2" />
        <S className="h-3.5 w-28" />
      </div>
    </div>
    <div className="flex gap-3 flex-wrap">
      <S className="h-8 w-24 rounded-lg" />
      <S className="h-8 w-32 rounded-lg" />
      <S className="h-8 w-24 rounded-lg" />
    </div>
  </div>
);

const SpecialtyCardSkeleton = ({ isPrimary = false }: { isPrimary?: boolean }) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm mb-4">
    <div className="bg-gray-300 h-11" />
    <div className="bg-white p-5">
      <S className="h-3.5 w-full mb-1.5" />
      <S className="h-3.5 w-[90%] mb-1.5" />
      <S className="h-3.5 w-[70%] mb-4" />
      <MatchedProviderSkeleton avatarSize={isPrimary ? 'h-14 w-14' : 'h-10 w-10'} />
    </div>
  </div>
);

const ResourceItemSkeleton = () => (
  <div className="rounded-lg border border-gray-200 p-3">
    <S className="h-3 w-3/4 mb-1.5" />
    <S className="h-3 w-1/2 mb-1.5" />
    <S className="h-3 w-1/3" />
  </div>
);

export const CarePlanSkeleton = () => (
  <div className="min-h-screen bg-background animate-pulse">
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-2 px-2 py-2 items-start">

      {/* ── Unified planner card: sections sidebar + content ──────────── */}
      <div className="flex flex-col lg:flex-row items-start bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden lg:overflow-visible">

        {/* LEFT SIDEBAR */}
        <aside className="hidden lg:flex lg:flex-col lg:w-[220px] lg:flex-shrink-0 border-r border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100">
            <S className="h-3.5 w-32" />
          </div>
          <div className="px-3 py-3 space-y-1">
            <NavItemSkeleton />
            <NavItemSkeleton />
            <NavItemSkeleton indent />
            <NavItemSkeleton indent />
            <NavItemSkeleton indent />
          </div>
        </aside>

        {/* MIDDLE: overview & care team */}
        <main className="min-w-0 flex-1 w-full p-6">

          {/* Overview & Summary */}
          <div className="pb-8 mb-8 border-b border-gray-100">
            <S className="h-6 w-72 mb-2" />
            <S className="h-3.5 w-48 mb-4" />
            <div className="rounded-2xl border border-gray-200 px-8 py-6 mb-6">
              <S className="h-3.5 w-full mb-2" />
              <S className="h-3.5 w-[92%] mb-2" />
              <S className="h-3.5 w-[60%] mb-4" />
              <S className="h-3.5 w-full mb-2" />
              <S className="h-3.5 w-[80%]" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <S className="h-9 w-48 rounded-md" />
              <S className="h-9 w-40 rounded-md" />
            </div>
          </div>

          {/* Your Care Team */}
          <S className="h-6 w-28 rounded mb-2.5" />
          <SpecialtyCardSkeleton isPrimary />

          <div className="flex flex-col gap-4">
            <SpecialtyCardSkeleton />
            <SpecialtyCardSkeleton />
          </div>
        </main>
      </div>

      {/* RIGHT SIDEBAR: Resources about your care stage */}
      <aside>
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="p-5">
            <S className="h-4 w-56 mb-2" />
            <S className="h-3 w-64 mb-4" />
            <div className="space-y-3">
              <ResourceItemSkeleton />
              <ResourceItemSkeleton />
              <ResourceItemSkeleton />
              <ResourceItemSkeleton />
            </div>
          </div>
        </div>
      </aside>

    </div>
  </div>
);
